import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { setSocketToken } from "../api/rooms";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { Logo } from "../components/Logo";
import { inertAttrs, inertOnClick } from "../logic/inert";

function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

export function LoginPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnTo(
    new URLSearchParams(location.search).get("returnTo"),
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const name = username.trim();
    if (!name || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.login(name, password);
      setSocketToken(resp.socket_token);
      navigate(returnTo ?? "/lobby", { replace: true });
    } catch (err) {
      // Capacity (409) routes to /full via the api/client capacity
      // handler. Don't surface an error toast — the navigate fires
      // before this catch runs.
      if (err instanceof api.ApiError && err.status === 409) return;
      if (
        err instanceof api.ApiError &&
        err.status === 403 &&
        err.body &&
        typeof err.body === "object" &&
        (err.body as { error?: string }).error === "email_not_verified"
      ) {
        // The credentials are correct but the email isn't verified.
        // Send them to the check-your-email view; we don't know the
        // email here without an extra call, so the page falls back
        // to a generic message + resend takes an explicit address.
        navigate("/check-your-email", { replace: true });
        return;
      }
      if (
        err instanceof api.ApiError &&
        err.status === 403 &&
        err.body &&
        typeof err.body === "object" &&
        (err.body as { error?: string }).error === "banned"
      ) {
        // Credentials correct, but the account is banned. Bounce to
        // the dedicated screen — note that no session cookie is set
        // here, so /banned's Log Out button is a no-op-on-server
        // safety net and we just land them back on /login.
        navigate("/banned", { replace: true });
        return;
      }
      // Don't leak which of username vs password was wrong — the
      // server already does the same.
      const friendly =
        err instanceof api.ApiError && err.status === 401
          ? "Username or password is incorrect."
          : err instanceof api.ApiError && err.status === 429
            ? "Too many login attempts. Try again in a minute."
            : err instanceof Error
              ? err.message
              : String(err);
      setError(friendly);
      setSubmitting(false);
    }
  }

  async function onPlayAsGuest() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.loginAsGuest();
      setSocketToken(resp.socket_token);
      navigate(returnTo ?? "/lobby", { replace: true });
    } catch (err) {
      // Capacity (409) routes to /full via the api/client capacity
      // handler. Don't surface an error toast — the navigate fires
      // before this catch runs.
      if (err instanceof api.ApiError && err.status === 409) return;
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  const registerHref = returnTo
    ? `/register?returnTo=${encodeURIComponent(returnTo)}`
    : "/register";

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 420,
          display: "grid",
          gap: 18,
        }}
      >
        <Logo s={1.4} style={{ justifyContent: "center" }} />
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          USERNAME
          <input
            autoFocus
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            placeholder="your username"
            pattern="[A-Za-z0-9_\-]{3,32}"
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          PASSWORD
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder="your password"
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        {error && (
          <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
        )}
        <button
          type="submit"
          {...inertAttrs(submitting || username.trim().length < 3 || !password)}
          {...withBtnStyle(
            primaryBtn(theme, submitting || username.trim().length < 3 || !password),
            { fontSize: 12, padding: "10px 16px", cursor: submitting ? "wait" : undefined },
          )}
        >
          {submitting ? "…" : "LOG IN"}
        </button>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
          }}
        >
          <Link to={registerHref} style={{ color: theme.inkDim }}>
            Create an account
          </Link>
          <Link to="/forgot-password" style={{ color: theme.inkDim }}>
            Forgot password?
          </Link>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: theme.inkFaint,
            fontSize: 12,
          }}
        >
          <span style={{ flex: 1, height: 1, background: theme.panelEdge }} />
          OR
          <span style={{ flex: 1, height: 1, background: theme.panelEdge }} />
        </div>
        <button
          type="button"
          onClick={inertOnClick(submitting, onPlayAsGuest)}
          {...inertAttrs(submitting)}
          {...withBtnStyle(defaultBtn(theme, submitting), {
            fontSize: 12,
            padding: "10px 16px",
            cursor: submitting ? "wait" : undefined,
          })}
        >
          PLAY AS GUEST
        </button>
        <div style={{ fontSize: 13, color: theme.inkFaint, textAlign: "center" }}>
          Try it without signing up. Your XP and packs save if you make
          an account later.
        </div>
      </form>
    </div>
  );
}
